import React from "react";

import { DEFAULT_LOCALE, type SupportedLocale } from "./constants";
import { getClientLocaleHint } from "./detect-locale";
import { allMessages } from "./messages";
import { saveStoredLocale } from "./storage";

import type { MessageKey, TranslationValues } from "./types";

type I18nContextValue = {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  t: (key: MessageKey, values?: TranslationValues) => string;
};

const I18nContext = React.createContext<I18nContextValue | undefined>(undefined);

function resolveMessage(key: MessageKey, locale: SupportedLocale): string {
  const segments = key.split(".");
  let current: unknown = allMessages[locale];

  for (const segment of segments) {
    if (!current || typeof current !== "object" || !(segment in current)) {
      current = undefined;
      break;
    }

    current = (current as Record<string, unknown>)[segment];
  }

  if (typeof current === "string") {
    return current;
  }

  if (locale !== DEFAULT_LOCALE) {
    return resolveMessage(key, DEFAULT_LOCALE);
  }

  return key;
}

function interpolate(message: string, values?: TranslationValues): string {
  if (!values) {
    return message;
  }

  return message.replace(/\{\{\s*([^{}]+?)\s*\}\}|\{\s*([^{}]+?)\s*\}/g, (_, doubleBraceKey: string, singleBraceKey: string) => {
    const rawKey = doubleBraceKey ?? singleBraceKey;
    const value = values[rawKey.trim()];
    return value === undefined || value === null ? "" : String(value);
  });
}

function createTranslator(locale: SupportedLocale) {
  return (key: MessageKey, values?: TranslationValues) => {
    return interpolate(resolveMessage(key, locale), values);
  };
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  // Sempre o mesmo valor no SSR e no primeiro paint do cliente — evita hydration mismatch.
  // O locale real (banco > navegador > localStorage) entra depois, só no cliente.
  const [locale, setLocaleState] = React.useState<SupportedLocale>(DEFAULT_LOCALE);

  React.useEffect(() => {
    setLocaleState(getClientLocaleHint());
  }, []);

  React.useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = React.useCallback((nextLocale: SupportedLocale) => {
    saveStoredLocale(nextLocale);
    setLocaleState(nextLocale);
  }, []);

  const t = React.useMemo(() => createTranslator(locale), [locale]);

  const value = React.useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t,
    }),
    [locale, setLocale, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Variante opcional: retorna undefined fora do I18nProvider em vez de lançar.
 * Útil para componentes de UI compartilhados (ex.: Button) que precisam funcionar sem provider.
 */
export function useI18nOptional() {
  return React.useContext(I18nContext);
}

export function useI18nContext() {
  const context = React.useContext(I18nContext);

  if (!context) {
    throw new Error("useI18n must be used within I18nProvider");
  }

  return context;
}

export function useI18n() {
  return useI18nContext();
}
